/**
 * <s-nav-flyout> — category list with a side pane of products for the header Products menu.
 *
 * Required child markup:
 *   button[data-fly-cat]  — a category row; `aria-controls` holds its pane id.
 *   a[data-fly-leaf]      — a category without products (plain link).
 *   .live-fly-pane[id]    — product pane for one category, toggled with `hidden`.
 *
 * The active category is sticky: it stays open until another category is chosen, so the
 * pointer can cross other rows on its way to the products. Mouse hover switches after a short
 * intent delay (instant when nothing is open yet); entering the pane cancels a pending switch.
 * Click, tap, Enter and Space switch immediately. State resets when the parent dropdown closes.
 */
class SNavFlyout extends HTMLElement {
  #ac;
  #timer = 0;
  /** @type {HTMLElement | null} */
  #active = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const buttons = this.querySelectorAll('[data-fly-cat]');

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      button.addEventListener('click', () => this.#activate(button), opts);
      button.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.#schedule(button);
        },
        opts
      );
    }

    for (const leaf of this.querySelectorAll('[data-fly-leaf]')) {
      leaf.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.#schedule(null);
        },
        opts
      );
    }

    const panes = this.querySelector('.live-fly-panes');
    panes?.addEventListener('pointerenter', () => this.#cancel(), opts);

    // Reset when the whole dropdown closes so it reopens as a clean list.
    const dropdown = this.closest('s-nav-dropdown');
    dropdown?.addEventListener(
      'mouseleave',
      () => {
        if (!dropdown.hasAttribute('data-open')) this.#reset();
      },
      opts
    );
    dropdown?.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget instanceof Node && dropdown.contains(event.relatedTarget)) return;
        if (!dropdown.matches(':hover')) this.#reset();
      },
      opts
    );
  }

  disconnectedCallback() {
    this.#cancel();
    this.#ac?.abort();
  }

  /** @param {HTMLElement | null} button */
  #schedule(button) {
    this.#cancel();
    if (button === this.#active) return;
    const delay = this.#active ? 140 : 0;
    this.#timer = window.setTimeout(() => this.#activate(button), delay);
  }

  #cancel() {
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }

  #reset() {
    this.#cancel();
    this.#activate(null);
  }

  /** @param {HTMLElement | null} button */
  #activate(button) {
    this.#cancel();
    if (this.#active && this.#active !== button) {
      this.#active.setAttribute('aria-expanded', 'false');
      const oldPane = this.#paneFor(this.#active);
      if (oldPane) oldPane.hidden = true;
    }
    this.#active = button;
    if (!button) {
      this.removeAttribute('data-active');
      return;
    }
    button.setAttribute('aria-expanded', 'true');
    const pane = this.#paneFor(button);
    if (pane) {
      pane.hidden = false;
      pane.scrollTop = 0;
    }
    this.setAttribute('data-active', '');
  }

  /** @param {HTMLElement} button */
  #paneFor(button) {
    const id = button.getAttribute('aria-controls');
    return id ? this.querySelector(`[id="${id}"]`) : null;
  }
}

customElements.define('s-nav-flyout', SNavFlyout);
